import { Nullable } from './utils';

export const enum CrmProvider {
  salesforce = 'salesforce',
  hubspot = 'hubspot',
  pipedrive = 'pipedrive',
  zoho = 'zoho'
}
export interface ICrmSettings {
  uuid: string;
  advancedForecastSubmissionEnabled: boolean;
  defaultAccountTimezone: string;
  crmCustomCurrencyCode?: string;
}

export interface AppExchangeApiKey {
  uuid: string;
  clientId: string;
  apiKey: string;
  apiKeyModifiedBy: string;
  apiKeyModifiedAt: string;
  apiKeyConnectedEmail: Nullable<string>;
}

export interface CrmPipelineStage {
  uuid: string;
  externalId: string;
  apiName: string;
  defaultProbability: number;
  isActive: boolean;
  isClosed: boolean;
  isWon: boolean;
  masterLabel: string;
  sortOrder: number;
  forecastCategory: Nullable<string>;
  forecastCategoryName: Nullable<string>;
  pipelineId: string;
  pipelineLabel: string;
}

export interface CrmUser {
  email: string;
  firstName: string;
  lastName: string;
  uuid: string;
  externalId: string;
  createdBySource: string;
  modifiedBySource: string;
  profilePic: Nullable<string>;
  isActive: boolean;
}

export interface IMappedPlaybook {
  playbookUuid: string;
  playbookLabel: string;
  playbookKey: string;
  isDefault: boolean;
  playbookSettingUuid: string;
  isActive: boolean;
}

export interface IPipeline {
  uuid: string;
  externalId: string;
  label: string;
  displayOrder: number;
  stages: CrmPipelineStage[];
  mappedPlaybooks: IMappedPlaybook[];
}

export const enum ECrmFieldEditAccess {
  everyone = 'everyone',
  adminOnly = 'admin_only',
  viewOnly = 'view_only'
}

export const CrmFieldEditAccessList = [
  ECrmFieldEditAccess.everyone,
  ECrmFieldEditAccess.adminOnly,
  ECrmFieldEditAccess.viewOnly
];

export interface ICrmAvomaField {
  uuid: string;
  fieldId: string;
  fieldLabel: string;
  dataType: string;
  fieldType: string;
  fieldClass: string;
  sortOrder: number;
  backgroundColor: string;
  optionsJson: string[];
  groupName: string;
  isMapped: boolean;
}

export interface ICrmField {
  uuid: string;
  externalId: string;
  fieldLabel: string;
  fieldType: string;
  fieldTypeLabel: string;
  dataType: string;
  optionsJson: {
    label: string;
    value: string;
    hidden: boolean;
    active?: boolean;
    displayOrder: number;
  }[];
  sortOrder: number;
  isDefault: boolean;
  isAvomaDefined: boolean;
  isReadOnly: boolean;
  syncDirection: string;
  syncSetting: string;
  isCustomInCrm: boolean;
  isCalculated: boolean;
  createdByAtSource: string;
  editableBy: string;
}

export interface ICrmFieldSummary {
  uuid: string;
  externalId?: string | null;
  fieldLabel?: string | null;
}

export interface ICrmFieldAuditLog {
  userInfo?: unknown;
  created?: string;
}

export interface ICrmOrgSettingsData {
  crmCustomAmountField?: ICrmFieldSummary | null;
  crmCustomCloseDateField?: ICrmFieldSummary | null;
  crmCustomForecastCategoryField?: ICrmFieldSummary | null;
  crmCustomAmountFieldAuditLogs?: ICrmFieldAuditLog[];
  crmCustomCloseDateFieldAuditLogs?: ICrmFieldAuditLog[];
  crmCustomForecastCategoryFieldAuditLogs?: ICrmFieldAuditLog[];
}

export interface ICrmFieldOption {
  id?: string;
  uuid?: string;
  externalId?: string;
  label: string;
  value?: string;
  hidden?: boolean;
  active?: boolean;
  displayOrder?: number;
}

export interface ICrmFieldMapping {
  uuid: string;
  avomaField: Nullable<ICrmAvomaField>;
  contentType: number;
  crmField: ICrmField;
  objectType: string;
  createdBy: string;
  groupName: string;
  isMappable: boolean;
}

export interface ICrmEventFieldForMeetingType {
  uuid: string;
  externalId: string;
  fieldLabel: string;
  fieldType: string;
  fieldTypeLabel: string;
  dataType: string;
  description: Nullable<string>;
  createdByAtSource: string;
  meetingTypeUuids: string[];
  meetingTypesCount: number;
  created: string;
  modified: string;
}

export interface ICrmEventFieldsForMeetingTypeResponse {
  crmEventFields: ICrmEventFieldForMeetingType[];
  totalFields: number;
  totalMeetingTypes: number;
}

export interface ICrmTaskFieldForMeetingType {
  uuid: string;
  externalId: string;
  fieldLabel: string;
  fieldType: string;
  fieldTypeLabel: string;
  dataType: string;
  description: Nullable<string>;
  createdByAtSource: string;
  meetingTypeUuids: string[];
  meetingTypesCount: number;
  created: string;
  modified: string;
}

export interface ICrmTaskFieldsForMeetingTypeResponse {
  crmTaskFields: ICrmTaskFieldForMeetingType[];
  totalFields: number;
  totalMeetingTypes: number;
}

// Outcomes mapping types (parallel to MeetingType)
export interface ICrmEventFieldForMeetingOutcome {
  uuid: string;
  externalId: string;
  fieldLabel: string;
  fieldType: string;
  fieldTypeLabel: string;
  dataType: string;
  description: Nullable<string>;
  createdByAtSource: string;
  meetingOutcomeUuids: string[];
  meetingOutcomesCount: number;
  created: string;
  modified: string;
}

export interface ICrmEventFieldsForMeetingOutcomeResponse {
  crmEventFields: ICrmEventFieldForMeetingOutcome[];
  totalFields: number;
  totalMeetingOutcomes: number;
}

export interface ICrmTaskFieldForMeetingOutcome {
  uuid: string;
  externalId: string;
  fieldLabel: string;
  fieldType: string;
  fieldTypeLabel: string;
  dataType: string;
  description: Nullable<string>;
  createdByAtSource: string;
  meetingOutcomeUuids: string[];
  meetingOutcomesCount: number;
  created: string;
  modified: string;
}

export interface ICrmTaskFieldsForMeetingOutcomeResponse {
  crmTaskFields: ICrmTaskFieldForMeetingOutcome[];
  totalFields: number;
  totalMeetingOutcomes: number;
}
